import { WORLD_SLUG, type WorldSlug } from '@vrlearning/lesson-schema';

/**
 * How high the pupil's eyes are above the real floor.
 *
 * The lectern follows the eyes and framing is computed for them
 * (docs/superpowers/specs/2026-09-28-clear-view-design.md), so a seated child
 * does not get a lectern at eye level. In a session it is the headset's own
 * pose; on the first frames of a session that is not known yet, and 1.6 m
 * stands in until it is -- the caller places the view again, without a fade,
 * the moment `observe` says it has arrived. A laptop uses the preview's height.
 *
 * A reading no pupil could give (NaN, a headset on the floor or held above a
 * tall adult) is ignored, and the height counts as known only after a few
 * steady frames: a headset finding its floor, or being put on, reports
 * heights that jump, and placing the view for one of those would put a
 * pupil in the wrong place until the next step.
 */

/** What a pupil's eyes can be: a small child sitting to a tall adult standing. */
export const EYE_MIN = 1.0;
export const EYE_MAX = 2.0;
/** Used until a headset says otherwise: an adult standing, which lessons were written for. */
export const EYE_ASSUMED = 1.6;
/** Outside this a reading is not a pupil's eyes at all, and is ignored. */
export const EYE_PLAUSIBLE_MIN = 0.9;
export const EYE_PLAUSIBLE_MAX = 2.1;
/** Readings in a row, each within `EYE_STEADY_METRES` of the first, before the height is known. */
export const EYE_STEADY_FRAMES = 10;
export const EYE_STEADY_METRES = 0.05;

const clamp = (n: number): number => Math.min(EYE_MAX, Math.max(EYE_MIN, n));

export class EyeHeight {
  #live: number | undefined;
  /** The steady run so far, before the height is known. */
  #run: { from: number; frames: number } | undefined;

  constructor(readonly preview = EYE_ASSUMED) {}

  /** The height to place a view and the lectern for, now. */
  current(presenting: boolean): number {
    if (!presenting) return this.preview;
    return this.#live ?? EYE_ASSUMED;
  }

  /**
   * A pose from the headset, metres above the floor. True the first time in a
   * session: the moment to place the view again.
   */
  observe(height: number): boolean {
    if (!(height >= EYE_PLAUSIBLE_MIN && height <= EYE_PLAUSIBLE_MAX)) return false;
    if (this.#live !== undefined) {
      this.#live = clamp(height);
      return false;
    }
    const run = this.#run;
    this.#run =
      run && Math.abs(height - run.from) <= EYE_STEADY_METRES
        ? { from: run.from, frames: run.frames + 1 }
        : { from: height, frames: 1 };
    if (this.#run.frames < EYE_STEADY_FRAMES) return false;
    this.#live = clamp(height);
    this.#run = undefined;
    return true;
  }

  /** The session ended; the next one starts unknown. */
  reset(): void {
    this.#live = undefined;
    this.#run = undefined;
  }
}

export type PreviewOptions = {
  /** Where the desktop camera stands: `?eye=1.3` shows what a child sees. */
  eye: number;
  /** `?frame=1`: draw the step's subject box and the lectern's clear line. */
  frame: boolean;
  /** `?look=flat`: the look before worlds (ADR-032), to compare. */
  look: 'full' | 'flat';
  /** `?perf=1`: frame time, draw calls and triangles, in the view (a headset too). */
  perf: boolean;
  /** `?world=<slug>`: a VR lesson tried in another world. */
  world?: WorldSlug;
  /** `?spannend=1`: a lesson's Spannend parts on from the start (ADR-043); calm by default. */
  spannend: boolean;
};

/** Reads `?eye=`, `?frame=1`, `?look=flat`, `?perf=1`, `?world=` and `?spannend=1` off the desktop preview's address. */
export function previewOptions(href: string = globalThis.location?.href ?? ''): PreviewOptions {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { eye: EYE_ASSUMED, frame: false, look: 'full', perf: false, spannend: false };
  }
  // A Dutch keyboard types a comma.
  const asked = Number.parseFloat((url.searchParams.get('eye') ?? '').replace(',', '.'));
  // Any slug-shaped value: a package's world too (a world it cannot build shows the studio).
  const askedWorld = url.searchParams.get('world');
  const world = askedWorld !== null && WORLD_SLUG.test(askedWorld) ? askedWorld : undefined;
  return {
    eye: Number.isFinite(asked) ? clamp(asked) : EYE_ASSUMED,
    frame: url.searchParams.get('frame') === '1',
    look: url.searchParams.get('look') === 'flat' ? 'flat' : 'full',
    perf: url.searchParams.get('perf') === '1',
    spannend: url.searchParams.get('spannend') === '1',
    ...(world ? { world } : {}),
  };
}
